import { RUNTIME_BACKEND_URL } from '../../lib/backendUrl';
import { useEffect, useMemo, useState, Fragment } from 'react';
import { useHesapAuth } from '../../store/hesapStore';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Sun, Zap, Battery, TrendingUp, Wallet, Calendar, Plus,
  Trash2, Edit2, Settings, Loader2, Save, BarChart3, CheckCircle2,
  Users, X, ChevronDown, ChevronRight, GitCompare, Radio, Copy, RefreshCw, Cpu, AlertTriangle,
  WifiOff, Moon, History, FileText
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, ReferenceLine
} from 'recharts';

const API = `${RUNTIME_BACKEND_URL}/api/hesap`;
const fmt = (n) => (Number(n) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtInt = (n) => (Number(n) || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 });

const MONTH_NAMES = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
const fmtPeriod = (p) => {
  if (!p || p.length < 7) return p;
  const [y, m] = p.split('-');
  return `${MONTH_NAMES[parseInt(m) - 1]} ${y}`;
};

const CURRENT_YEAR = new Date().getFullYear();

export default function HesapSolarPage() {
  const { token } = useHesapAuth();
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [config, setConfig] = useState({ system_name: '', install_date: '', panel_power_kwp: 0, investment_cost: 0, notes: '' });
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [compareData, setCompareData] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const [showLive, setShowLive] = useState(false);
  const [device, setDevice] = useState(null);
  const [liveStatus, setLiveStatus] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [boardType, setBoardType] = useState('esp32'); // 'esp32' | 'esp8266'
  const [showLogs, setShowLogs] = useState(false);
  const [alarmLogs, setAlarmLogs] = useState([]);

  const [showConfig, setShowConfig] = useState(false);
  const [showRec, setShowRec] = useState(false);
  const [showSubs, setShowSubs] = useState(false);
  const [editing, setEditing] = useState(null);
  const [recForm, setRecForm] = useState({
    period: new Date().toISOString().slice(0, 7),
    production_kwh: '', consumption_kwh: '', sell_price: '', buy_price: '', notes: '',
    subscriber_bills: []
  });
  const [saving, setSaving] = useState(false);

  const [subForm, setSubForm] = useState({ id: null, name: '', subscriber_no: '', meter_no: '', address: '', note: '' });

  const load = async () => {
    setLoading(true);
    try {
      const [cr, rr, sr, subs, cmp] = await Promise.all([
        fetch(`${API}/solar/config`, { headers }).then(r => r.ok ? r.json() : {}),
        fetch(`${API}/solar/records?year=${selectedYear}`, { headers }).then(r => r.ok ? r.json() : []),
        fetch(`${API}/solar/summary?year=${selectedYear}`, { headers }).then(r => r.ok ? r.json() : null),
        fetch(`${API}/solar/subscribers`, { headers }).then(r => r.ok ? r.json() : []),
        fetch(`${API}/solar/compare-years`, { headers }).then(r => r.ok ? r.json() : []),
      ]);
      setConfig(prev => ({ ...prev, ...cr }));
      setRecords(rr);
      setSummary(sr);
      setSubscribers(subs);
      setCompareData(cmp);
    } finally { setLoading(false); }
  };

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [selectedYear]);

  // Canlı durum polling (Solar sayfasında iken)
  useEffect(() => {
    if (!token) return;
    let cancel = false;
    const poll = async () => {
      try {
        const r = await fetch(`${API}/solar/live-status`, { headers });
        if (!r.ok) return;
        const d = await r.json();
        if (!cancel) setLiveStatus(d);
      } catch { /* ignore */ }
    };
    poll();
    const iv = setInterval(poll, 15000);
    return () => { cancel = true; clearInterval(iv); };
    // eslint-disable-next-line
  }, [token]);

  const openLiveTracking = async () => {
    setShowLive(true);
    try {
      const r = await fetch(`${API}/solar/device`, { headers });
      if (r.ok) setDevice(await r.json());
    } catch { /* ignore */ }
  };

  const rotateKey = async () => {
    if (!window.confirm('API key yenilenirse mevcut cihaz artık gönderim yapamaz. Devam?')) return;
    const r = await fetch(`${API}/solar/device/rotate-key`, { method: 'POST', headers });
    if (r.ok) { setDevice(await r.json()); toast.success('API key yenilendi'); }
  };

  const saveDeviceThreshold = async (secs) => {
    const r = await fetch(`${API}/solar/device`, {
      method: 'PUT', headers, body: JSON.stringify({ threshold_seconds: secs })
    });
    if (r.ok) { setDevice(await r.json()); toast.success('Eşik güncellendi'); }
  };

  const saveQuietHours = async () => {
    const r = await fetch(`${API}/solar/device`, {
      method: 'PUT', headers, body: JSON.stringify({
        quiet_hours_start: (device.quiet_hours_start || '').trim(),
        quiet_hours_end: (device.quiet_hours_end || '').trim(),
      })
    });
    if (r.ok) { setDevice(await r.json()); toast.success('Sessiz saatler kaydedildi'); }
    else { const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Kaydedilemedi'); }
  };

  const openLogs = async () => {
    setShowLogs(true);
    try {
      const r = await fetch(`${API}/solar/alarm-logs?limit=200`, { headers });
      if (r.ok) setAlarmLogs(await r.json());
    } catch { /* ignore */ }
  };

  const deleteLog = async (id) => {
    const r = await fetch(`${API}/solar/alarm-logs/${id}`, { method: 'DELETE', headers });
    if (r.ok) { setAlarmLogs(prev => prev.filter(l => l.id !== id)); toast.success('Silindi'); }
  };

  const clearAllLogs = async () => {
    if (!window.confirm('Tüm alarm log kayıtları silinsin mi?')) return;
    const r = await fetch(`${API}/solar/alarm-logs`, { method: 'DELETE', headers });
    if (r.ok) { setAlarmLogs([]); toast.success('Tüm loglar silindi'); }
  };

  const copyText = (txt, label = 'Kopyalandı') => {
    navigator.clipboard?.writeText(txt);
    toast.success(label);
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/solar/config`, { method: 'PUT', headers, body: JSON.stringify(config) });
      if (!r.ok) throw new Error('Kaydedilemedi');
      toast.success('Sistem bilgileri güncellendi');
      setShowConfig(false);
      load();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const openNewRec = () => {
    setEditing(null);
    setRecForm({
      period: `${selectedYear}-${String(new Date().getMonth() + 1).padStart(2, '0')}`,
      production_kwh: '', consumption_kwh: '', sell_price: '', buy_price: '', notes: '',
      subscriber_bills: []
    });
    setShowRec(true);
  };

  const openEditRec = (r) => {
    setEditing(r);
    setRecForm({
      period: r.period,
      production_kwh: r.production_kwh,
      consumption_kwh: r.base_consumption_kwh ?? r.consumption_kwh,
      sell_price: r.sell_price,
      buy_price: r.buy_price,
      notes: r.notes || '',
      subscriber_bills: r.subscriber_bills || []
    });
    setShowRec(true);
  };

  const addBillRow = () => {
    const first = subscribers[0];
    setRecForm(f => ({
      ...f,
      subscriber_bills: [...f.subscriber_bills, {
        subscriber_id: first?.id || '',
        name: first?.name || '',
        subscriber_no: first?.subscriber_no || '',
        kwh: '', amount: '', include_in_total: true, note: ''
      }]
    }));
  };

  const updateBillRow = (idx, patch) => {
    setRecForm(f => ({
      ...f,
      subscriber_bills: f.subscriber_bills.map((b, i) => i === idx ? { ...b, ...patch } : b)
    }));
  };

  const removeBillRow = (idx) => {
    setRecForm(f => ({ ...f, subscriber_bills: f.subscriber_bills.filter((_, i) => i !== idx) }));
  };

  const onBillSubscriberChange = (idx, sid) => {
    const s = subscribers.find(x => x.id === sid);
    updateBillRow(idx, { subscriber_id: sid, name: s?.name || '', subscriber_no: s?.subscriber_no || '' });
  };

  const saveRec = async () => {
    if (!recForm.period) { toast.error('Dönem gerekli'); return; }
    setSaving(true);
    try {
      const payload = {
        ...recForm,
        subscriber_bills: recForm.subscriber_bills.map(b => ({ ...b, kwh: Number(b.kwh) || 0, amount: Number(b.amount) || 0 }))
      };
      const r = await fetch(`${API}/solar/records`, { method: 'POST', headers, body: JSON.stringify(payload) });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.detail || 'Kaydedilemedi'); }
      toast.success(editing ? 'Kayıt güncellendi' : 'Kayıt eklendi');
      setShowRec(false);
      load();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const removeRec = async (r) => {
    if (!window.confirm(`${fmtPeriod(r.period)} kaydı silinsin mi?`)) return;
    const res = await fetch(`${API}/solar/records/${r.id}`, { method: 'DELETE', headers });
    if (res.ok) { toast.success('Silindi'); load(); }
    else toast.error('Silinemedi');
  };

  const saveSubscriber = async () => {
    if (!subForm.name?.trim()) { toast.error('Abone adı gerekli'); return; }
    try {
      const url = subForm.id ? `${API}/solar/subscribers/${subForm.id}` : `${API}/solar/subscribers`;
      const method = subForm.id ? 'PUT' : 'POST';
      const r = await fetch(url, { method, headers, body: JSON.stringify(subForm) });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.detail || 'Kaydedilemedi'); }
      toast.success(subForm.id ? 'Abone güncellendi' : 'Abone eklendi');
      setSubForm({ id: null, name: '', subscriber_no: '', meter_no: '', address: '', note: '' });
      load();
    } catch (e) { toast.error(e.message); }
  };

  const removeSubscriber = async (s) => {
    if (!window.confirm(`${s.name} abonesi silinsin mi?`)) return;
    const r = await fetch(`${API}/solar/subscribers/${s.id}`, { method: 'DELETE', headers });
    if (r.ok) { toast.success('Silindi'); load(); }
  };

  const chartData = useMemo(() => records.map(r => ({
    period: fmtPeriod(r.period),
    Üretim: Number(r.production_kwh) || 0,
    Tüketim: Number(r.consumption_kwh) || 0,
    Kazanç: Number(r.total_gain) || 0,
  })), [records]);

  const cumulativeData = useMemo(() => (summary?.cumulative || []).map(c => ({
    period: fmtPeriod(c.period),
    'Kümülatif Kazanç': Number(c.cumulative_gain) || 0,
    'Yatırım': Number(c.investment_line) || 0,
  })), [summary]);

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-emerald-600" /></div>;
  }

  const t = summary?.totals || {};
  const paybackReached = summary && summary.remaining_investment <= 0;
  const availableYears = summary?.available_years?.length ? summary.available_years : [CURRENT_YEAR];
  const yearOptions = Array.from(new Set([CURRENT_YEAR, ...availableYears])).sort((a, b) => b - a);

  // Anlık önizleme — abone tüketimleri dahil
  const extraBillKwh = (recForm.subscriber_bills || []).filter(b => b.include_in_total).reduce((s, b) => s + (Number(b.kwh) || 0), 0);
  const totalCons = (Number(recForm.consumption_kwh) || 0) + extraBillKwh;
  const netKwh = (Number(recForm.production_kwh) || 0) - totalCons;

  return (
    <div className="space-y-4" data-testid="hesap-solar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <Sun className="h-6 w-6 text-yellow-500" />
            Solar System
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            {config.system_name || 'Sistem yapılandırılmamış'} · Yatırım: {fmt(config.investment_cost)} TL · {fmt(config.panel_power_kwp)} kWp
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex items-center gap-1.5 bg-slate-100 dark:bg-slate-800 rounded-lg px-2 py-1">
            <Calendar className="h-3.5 w-3.5 text-slate-500" />
            <span className="text-xs text-slate-500">Yıl:</span>
            <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
              <SelectTrigger className="h-7 w-[90px] border-0 bg-transparent" data-testid="solar-year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {yearOptions.map(y => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={openLiveTracking} data-testid="solar-live-btn" className={liveStatus?.alarm ? 'border-red-400 text-red-600' : liveStatus?.active ? 'border-emerald-400 text-emerald-600' : ''}>
            <Radio className="h-4 w-4 mr-1" />Canlı Takip
            {liveStatus?.active && <span className="ml-1 relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span><span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span></span>}
            {liveStatus?.alarm && <AlertTriangle className="h-3.5 w-3.5 ml-1 text-red-500" />}
          </Button>
          <Button variant="outline" onClick={() => setShowCompare(true)} data-testid="solar-compare-btn" disabled={compareData.length < 2}>
            <GitCompare className="h-4 w-4 mr-1" />Yıl Kıyasla
          </Button>
          <Button variant="outline" onClick={() => setShowSubs(true)} data-testid="solar-subs-btn">
            <Users className="h-4 w-4 mr-1" />Aboneler ({subscribers.length})
          </Button>
          <Button variant="outline" onClick={() => setShowConfig(true)} data-testid="solar-config-btn">
            <Settings className="h-4 w-4 mr-1" />Sistem Bilgileri
          </Button>
          <Button onClick={openNewRec} className="bg-emerald-600 hover:bg-emerald-700" data-testid="solar-new-record">
            <Plus className="h-4 w-4 mr-1" />Aylık Kayıt Ekle
          </Button>
        </div>
      </div>

      {/* Yıl banner */}
      <div className="bg-gradient-to-r from-emerald-50 to-yellow-50 dark:from-emerald-900/20 dark:to-yellow-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg px-3 py-2 flex items-center justify-between">
        <div className="text-sm text-slate-700 dark:text-slate-200">
          <b>{selectedYear}</b> yılı özeti gösteriliyor · <span className="text-slate-500">{summary?.months_count || 0} ay kaydı</span>
        </div>
        {selectedYear !== CURRENT_YEAR && (
          <Button size="sm" variant="ghost" onClick={() => setSelectedYear(CURRENT_YEAR)} className="h-7 text-xs">
            Güncel yıla dön
          </Button>
        )}
      </div>

      {/* Canlı durum özet kartı (Solar sayfasında) */}
      {liveStatus && liveStatus.configured && (() => {
        const stateColors = {
          producing: { border: 'border-emerald-400', bg: 'bg-emerald-100', text: 'text-emerald-700 dark:text-emerald-300', label: 'Üretim Aktif', icon: Zap },
          stopped:   { border: 'border-red-400',     bg: 'bg-red-100',     text: 'text-red-700 dark:text-red-300',     label: 'Üretim Durdu', icon: AlertTriangle },
          offline:   { border: 'border-red-500',     bg: 'bg-red-100',     text: 'text-red-700 dark:text-red-300',     label: 'Cihaz Bağlantısı Kesik', icon: WifiOff },
          quiet:     { border: 'border-amber-400',   bg: 'bg-amber-100',   text: 'text-amber-700 dark:text-amber-300', label: 'Sessiz Saatler', icon: Moon },
          unconfigured: { border: '', bg: 'bg-slate-100', text: 'text-slate-600', label: 'Beklemede', icon: Radio },
        };
        const sc = stateColors[liveStatus.state] || stateColors.unconfigured;
        const Icon = sc.icon;
        return (
          <Card className={sc.border ? `${sc.border} border-2` : ''}>
            <CardContent className="p-3 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg ${sc.bg} ${sc.text} flex items-center justify-center`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-bold ${sc.text}`}>{sc.label}</span>
                    {liveStatus.state === 'producing' && (
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    {liveStatus.last_watts > 0 && <>Anlık: <b>{fmt(liveStatus.last_watts)} W</b> · </>}
                    {liveStatus.seconds_ago != null ? `Son sinyal: ${liveStatus.seconds_ago < 60 ? `${liveStatus.seconds_ago} sn` : `${Math.floor(liveStatus.seconds_ago / 60)} dk`} önce` : 'Henüz sinyal yok'}
                    {' · '}Eşik: {liveStatus.threshold_seconds}sn
                  </p>
                </div>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={openLogs} data-testid="solar-open-logs">
                  <History className="h-3.5 w-3.5 mr-1" />Log
                </Button>
                <Button size="sm" variant="outline" onClick={openLiveTracking}>
                  <Settings className="h-3.5 w-3.5 mr-1" />Ayarlar
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Dashboard Kartları */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Zap} color="bg-yellow-100 text-yellow-700" label="Toplam Üretim" value={fmtInt(t.production_kwh)} unit="kWh" />
        <StatCard icon={Battery} color="bg-blue-100 text-blue-700" label="Toplam Tüketim" value={fmtInt(t.consumption_kwh)} unit="kWh" />
        <StatCard icon={TrendingUp} color="bg-emerald-100 text-emerald-700" label="Toplam Kazanç" value={fmt(t.total_gain)} unit="TL" big />
        <StatCard icon={Wallet} color="bg-violet-100 text-violet-700" label="Net Kazanç" value={fmt(t.net_gain)} unit="TL" big />
      </div>

      {/* Amortisman Kartı — TÜM zaman toplamı */}
      {config.investment_cost > 0 && summary && (
        <Card className={paybackReached ? 'border-emerald-500 border-2' : ''}>
          <CardContent className="p-4">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Amortisman Durumu (TÜM zamanların toplamı)</div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-center md:text-left">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Yatırım</p>
                <p className="text-xl font-bold text-slate-800 dark:text-slate-100">{fmt(config.investment_cost)} TL</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Kalan</p>
                <p className={`text-xl font-bold ${paybackReached ? 'text-emerald-600' : 'text-amber-600'}`}>
                  {fmt(summary.remaining_investment)} TL
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Yıllık Kazanç (Ort.)</p>
                <p className="text-xl font-bold text-emerald-600">{fmt(summary.yearly_gain)} TL</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Amortisman</p>
                {paybackReached ? (
                  <p className="text-xl font-bold text-emerald-600 flex items-center gap-1 justify-center md:justify-start">
                    <CheckCircle2 className="h-5 w-5" /> Tamamlandı
                  </p>
                ) : (
                  <p className="text-xl font-bold text-slate-800 dark:text-slate-100">
                    {summary.payback_years > 0 ? `${fmt(summary.payback_years)} yıl` : '-'}
                  </p>
                )}
                <p className="text-[10px] text-slate-500 mt-0.5">ROI: %{fmt(summary.roi_pct)}</p>
              </div>
            </div>
            <div className="mt-3">
              <div className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600" style={{ width: `${Math.min(100, summary.roi_pct)}%` }} />
              </div>
              <p className="text-[10px] text-slate-500 mt-1 text-center">
                Yatırımın %{fmt(Math.min(100, summary.roi_pct))}'ı geri kazanıldı
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Grafik 1: Aylık Üretim vs Tüketim */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-blue-600" />{selectedYear} — Aylık Üretim vs Tüketim (kWh)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {chartData.length === 0 ? (
            <p className="text-center text-sm text-slate-400 py-12">{selectedYear} yılına ait kayıt bulunamadı. Yeni bir kayıt ekleyebilirsiniz.</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => `${fmtInt(v)} kWh`} />
                <Legend />
                <Bar dataKey="Üretim" fill="#eab308" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Tüketim" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Grafik 2: Kümülatif Kazanç */}
      {config.investment_cost > 0 && cumulativeData.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-emerald-600" />{selectedYear} — Kümülatif Kazanç
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={cumulativeData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => `${fmt(v)} TL`} />
                <Legend />
                <ReferenceLine y={config.investment_cost} stroke="#dc2626" strokeDasharray="5 5" label={{ value: 'Yatırım Sınırı', position: 'insideTopRight', fill: '#dc2626', fontSize: 10 }} />
                <Line type="monotone" dataKey="Kümülatif Kazanç" stroke="#10b981" strokeWidth={3} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Kayıt Listesi */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Calendar className="h-4 w-4" />{selectedYear} Aylık Kayıtları ({records.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {records.length === 0 ? (
            <p className="text-center text-sm text-slate-400 py-8">
              {selectedYear} için henüz kayıt yok. <button className="text-emerald-600 underline" onClick={openNewRec}>İlk kaydı ekle</button>
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th className="w-8"></th>
                    <th className="text-left p-3">Dönem</th>
                    <th className="text-right p-3">Üretim</th>
                    <th className="text-right p-3">Tüketim</th>
                    <th className="text-right p-3">Net</th>
                    <th className="text-center p-3 hidden md:table-cell">Abone Fat.</th>
                    <th className="text-right p-3 hidden md:table-cell">Alış Fiyat</th>
                    <th className="text-right p-3 hidden md:table-cell">Satış Fiyat</th>
                    <th className="text-right p-3">Kazanç</th>
                    <th className="text-right p-3">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map(r => {
                    const isOpen = expandedIds.has(r.id);
                    const bills = r.subscriber_bills || [];
                    const hasDetails = bills.length > 0 || r.notes;
                    return (
                      <Fragment key={r.id}>
                      <tr className={`border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40 ${isOpen ? 'bg-slate-50/70 dark:bg-slate-800/40' : ''}`}>
                        <td className="p-1 text-center">
                          {hasDetails ? (
                            <button onClick={() => toggleExpand(r.id)} className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 p-1" data-testid={`expand-${r.id}`}>
                              {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </button>
                          ) : <span className="text-slate-300 text-xs">·</span>}
                        </td>
                        <td className="p-3 font-medium">{fmtPeriod(r.period)}</td>
                        <td className="p-3 text-right text-yellow-700 font-mono">{fmtInt(r.production_kwh)}</td>
                        <td className="p-3 text-right text-blue-700 font-mono">{fmtInt(r.consumption_kwh)}</td>
                        <td className={`p-3 text-right font-mono font-semibold ${r.net_kwh >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {r.net_kwh >= 0 ? '+' : ''}{fmtInt(r.net_kwh)}
                        </td>
                        <td className="p-3 text-center hidden md:table-cell">
                          {bills.length > 0 ? (
                            <button onClick={() => toggleExpand(r.id)} className="inline-flex items-center gap-1 text-xs bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300 rounded-full px-2 py-0.5 hover:bg-violet-200">
                              <Users className="h-3 w-3" />{bills.length}
                            </button>
                          ) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="p-3 text-right text-slate-500 text-xs hidden md:table-cell">{fmt(r.buy_price)} ₺</td>
                        <td className="p-3 text-right text-slate-500 text-xs hidden md:table-cell">{fmt(r.sell_price)} ₺</td>
                        <td className="p-3 text-right font-semibold text-emerald-700">{fmt(r.total_gain)} ₺</td>
                        <td className="p-3 text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEditRec(r)}>
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => removeRec(r)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr key={`${r.id}-detail`} className="bg-violet-50/40 dark:bg-violet-900/10 border-b border-slate-100 dark:border-slate-800">
                          <td></td>
                          <td colSpan={9} className="p-3">
                            <div className="space-y-2">
                              <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                                <span>Ana Tüketim: <b className="text-slate-700 dark:text-slate-200">{fmtInt(r.base_consumption_kwh ?? r.consumption_kwh)} kWh</b></span>
                                {bills.length > 0 && (
                                  <>
                                    <span className="text-slate-300">·</span>
                                    <span>Abone Toplam: <b className="text-violet-700">{fmtInt(bills.filter(b => b.include_in_total).reduce((s, b) => s + (Number(b.kwh) || 0), 0))} kWh</b></span>
                                    <span className="text-slate-300">·</span>
                                    <span>Genel Tüketim: <b className="text-blue-700">{fmtInt(r.consumption_kwh)} kWh</b></span>
                                  </>
                                )}
                              </div>
                              {bills.length > 0 ? (
                                <div className="overflow-x-auto">
                                  <table className="w-full text-xs bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
                                    <thead className="bg-violet-100 dark:bg-violet-900/30 text-violet-800 dark:text-violet-200">
                                      <tr>
                                        <th className="text-left p-2">Abone</th>
                                        <th className="text-left p-2">Abone No</th>
                                        <th className="text-right p-2">Tüketim</th>
                                        <th className="text-right p-2">Fatura</th>
                                        <th className="text-center p-2">Toplama</th>
                                        <th className="text-left p-2 hidden sm:table-cell">Not</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {bills.map((b, i) => (
                                        <tr key={i} className="border-t border-slate-100 dark:border-slate-700">
                                          <td className="p-2 font-medium">{b.name || '-'}</td>
                                          <td className="p-2 font-mono">{b.subscriber_no || '-'}</td>
                                          <td className="p-2 text-right font-mono text-blue-700">{fmtInt(b.kwh)} kWh</td>
                                          <td className="p-2 text-right font-mono">{fmt(b.amount)} ₺</td>
                                          <td className="p-2 text-center">
                                            {b.include_in_total ? (
                                              <span className="inline-flex items-center text-emerald-600" title="Toplama dahil"><CheckCircle2 className="h-3.5 w-3.5" /></span>
                                            ) : <span className="text-slate-300">—</span>}
                                          </td>
                                          <td className="p-2 text-slate-500 hidden sm:table-cell">{b.note || '-'}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <p className="text-xs text-slate-500 italic">Bu ay için abone fatura kaydı yok.</p>
                              )}
                              {r.notes && (
                                <div className="text-xs text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 rounded p-2 border border-slate-200 dark:border-slate-700">
                                  <b>Not:</b> {r.notes}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sistem Bilgileri Dialog */}
      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Settings className="h-5 w-5 text-emerald-600" />Sistem Bilgileri</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Sistem Adı</Label>
              <Input value={config.system_name || ''} onChange={e => setConfig({ ...config, system_name: e.target.value })} placeholder="Ör: Atölye Solar" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Kurulum Tarihi</Label>
                <Input type="date" value={config.install_date || ''} onChange={e => setConfig({ ...config, install_date: e.target.value })} />
              </div>
              <div>
                <Label>Panel Gücü (kWp)</Label>
                <Input type="number" step="0.01" value={config.panel_power_kwp || 0} onChange={e => setConfig({ ...config, panel_power_kwp: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Toplam Yatırım Maliyeti (TL)</Label>
              <Input type="number" step="0.01" value={config.investment_cost || 0} onChange={e => setConfig({ ...config, investment_cost: e.target.value })} data-testid="solar-invest" />
            </div>
            <div>
              <Label>Notlar</Label>
              <Input value={config.notes || ''} onChange={e => setConfig({ ...config, notes: e.target.value })} placeholder="İsteğe bağlı" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowConfig(false)}>İptal</Button>
              <Button onClick={saveConfig} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" />Kaydet</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Aboneler Dialog */}
      <Dialog open={showSubs} onOpenChange={setShowSubs}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-violet-600" />Aboneler (Tesisata bağlı sayaç/abone no)</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 bg-slate-50 dark:bg-slate-800/40">
              <div className="text-xs font-medium text-slate-600 dark:text-slate-300 mb-2">
                {subForm.id ? 'Aboneyi Düzenle' : 'Yeni Abone Ekle'}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Abone Adı *</Label>
                  <Input value={subForm.name} onChange={e => setSubForm({ ...subForm, name: e.target.value })} placeholder="Ör: Atölye, Ev, Depo" data-testid="sub-name" />
                </div>
                <div>
                  <Label className="text-xs">Abone No</Label>
                  <Input value={subForm.subscriber_no} onChange={e => setSubForm({ ...subForm, subscriber_no: e.target.value })} placeholder="4001234567" data-testid="sub-no" />
                </div>
                <div>
                  <Label className="text-xs">Sayaç No</Label>
                  <Input value={subForm.meter_no} onChange={e => setSubForm({ ...subForm, meter_no: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Adres/Not</Label>
                  <Input value={subForm.address} onChange={e => setSubForm({ ...subForm, address: e.target.value })} />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-2">
                {subForm.id && (
                  <Button size="sm" variant="ghost" onClick={() => setSubForm({ id: null, name: '', subscriber_no: '', meter_no: '', address: '', note: '' })}>İptal</Button>
                )}
                <Button size="sm" onClick={saveSubscriber} className="bg-violet-600 hover:bg-violet-700" data-testid="sub-save">
                  <Save className="h-3.5 w-3.5 mr-1" />{subForm.id ? 'Güncelle' : 'Ekle'}
                </Button>
              </div>
            </div>
            <div className="border rounded-lg overflow-hidden">
              {subscribers.length === 0 ? (
                <p className="text-center text-sm text-slate-400 py-6">Henüz abone yok.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-800 text-xs">
                    <tr>
                      <th className="text-left p-2">Ad</th>
                      <th className="text-left p-2">Abone No</th>
                      <th className="text-left p-2 hidden sm:table-cell">Sayaç No</th>
                      <th className="text-right p-2">İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscribers.map(s => (
                      <tr key={s.id} className="border-t border-slate-100 dark:border-slate-800">
                        <td className="p-2 font-medium">{s.name}</td>
                        <td className="p-2 font-mono text-xs">{s.subscriber_no || '-'}</td>
                        <td className="p-2 font-mono text-xs hidden sm:table-cell">{s.meter_no || '-'}</td>
                        <td className="p-2 text-right">
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setSubForm({ id: s.id, name: s.name, subscriber_no: s.subscriber_no || '', meter_no: s.meter_no || '', address: s.address || '', note: s.note || '' })}>
                            <Edit2 className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => removeSubscriber(s)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="flex justify-end pt-2">
              <Button variant="outline" onClick={() => setShowSubs(false)}>Kapat</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Alarm Log Dialog */}
      <Dialog open={showLogs} onOpenChange={setShowLogs}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History className="h-5 w-5 text-slate-600" />Solar Alarm / Durum Geçmişi</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-500">Son {alarmLogs.length} durum değişikliği (en yeni önce)</p>
              {alarmLogs.length > 0 && (
                <Button size="sm" variant="outline" onClick={clearAllLogs} className="text-red-600 hover:text-red-700 border-red-200" data-testid="clear-all-logs">
                  <Trash2 className="h-3.5 w-3.5 mr-1" />Tümünü Sil
                </Button>
              )}
            </div>
            {alarmLogs.length === 0 ? (
              <p className="text-center text-sm text-slate-400 py-8">Henüz log kaydı yok.</p>
            ) : (
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 dark:bg-slate-800">
                    <tr>
                      <th className="text-left p-2">Tarih / Saat</th>
                      <th className="text-left p-2">Geçiş</th>
                      <th className="text-left p-2">Durum</th>
                      <th className="text-left p-2">Mesaj</th>
                      <th className="text-right p-2 hidden sm:table-cell">Watt</th>
                      <th className="text-right p-2 w-12"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {alarmLogs.map(log => {
                      const stateColors = {
                        producing: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30',
                        stopped: 'text-red-600 bg-red-50 dark:bg-red-900/30',
                        offline: 'text-red-700 bg-red-100 dark:bg-red-900/40 font-bold',
                        quiet: 'text-amber-600 bg-amber-50 dark:bg-amber-900/30',
                        unconfigured: 'text-slate-500 bg-slate-100 dark:bg-slate-800',
                      };
                      const stateLabels = {
                        producing: '🟢 Üretim Aktif', stopped: '🔴 Üretim Durdu', offline: '⚠️ Bağlantı Kesik',
                        quiet: '🌙 Sessiz Saat', unconfigured: '⚫ Yapılandırılmamış',
                      };
                      const d = new Date(log.at);
                      return (
                        <tr key={log.id} className="border-t border-slate-100 dark:border-slate-800">
                          <td className="p-2 font-mono text-[10.5px] whitespace-nowrap">{d.toLocaleString('tr-TR')}</td>
                          <td className="p-2 text-slate-500 text-[10.5px] whitespace-nowrap">
                            {stateLabels[log.prev_state] || log.prev_state} → 
                          </td>
                          <td className="p-2">
                            <span className={`inline-block px-2 py-0.5 rounded ${stateColors[log.new_state] || ''}`}>
                              {stateLabels[log.new_state] || log.new_state}
                            </span>
                          </td>
                          <td className="p-2 text-slate-600 dark:text-slate-300">{log.message}</td>
                          <td className="p-2 text-right font-mono hidden sm:table-cell">{log.watts > 0 ? `${fmt(log.watts)} W` : '-'}</td>
                          <td className="p-2 text-right">
                            <Button size="icon" variant="ghost" className="h-6 w-6 text-red-500" onClick={() => deleteLog(log.id)} data-testid={`delete-log-${log.id}`}>
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <div className="flex justify-end pt-2">
              <Button variant="outline" onClick={() => setShowLogs(false)}>Kapat</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Canlı Takip / Arduino Ayarları Dialog */}
      <Dialog open={showLive} onOpenChange={setShowLive}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Radio className="h-5 w-5 text-emerald-600" />Canlı Takip · Arduino/ESP32 Ayarları</DialogTitle>
          </DialogHeader>
          {!device ? (
            <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>
          ) : (() => {
            // Arduino için TAM URL — kullanıcı public_base_url ayarladıysa onu, yoksa REACT_APP_BACKEND_URL, o da yoksa sayfa origin'i
            const baseUrl = (device.public_base_url || RUNTIME_BACKEND_URL || window.location.origin).replace(/\/$/, '');
            const fullTelemetryUrl = `${baseUrl}/api/hesap/solar/telemetry`;
            const isHttps = fullTelemetryUrl.startsWith('https://');
            return (
            <div className="space-y-4">
              {/* Anlık durum */}
              <div className={`rounded-lg p-3 ${liveStatus?.active ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200' : liveStatus?.alarm ? 'bg-red-50 dark:bg-red-900/20 border border-red-200' : 'bg-slate-50 dark:bg-slate-800/40 border border-slate-200'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {liveStatus?.active ? <Zap className="h-5 w-5 text-emerald-600" /> : liveStatus?.alarm ? <AlertTriangle className="h-5 w-5 text-red-600" /> : <Radio className="h-5 w-5 text-slate-500" />}
                    <div>
                      <div className="text-sm font-bold">
                        {liveStatus?.active ? 'Üretim Aktif' : liveStatus?.alarm ? 'Sinyal Kesildi / Üretim Durdu' : 'Beklemede'}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {liveStatus?.last_watts > 0 && <>Anlık: <b>{fmt(liveStatus.last_watts)} W</b> · </>}
                        Son sinyal: {liveStatus?.seconds_ago != null ? `${liveStatus.seconds_ago} sn önce` : 'Henüz yok'}
                      </div>
                    </div>
                  </div>
                  {liveStatus?.active && (
                    <span className="relative flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
                    </span>
                  )}
                </div>
              </div>

              {/* Public URL - Arduino'nun bağlanacağı tam adres */}
              <div>
                <Label className="flex items-center gap-1">🌐 Public URL (Arduino'nun ulaşacağı sunucu adresi)</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={device.public_base_url || ''}
                    onChange={e => setDevice({ ...device, public_base_url: e.target.value })}
                    placeholder={window.location.origin}
                    className="font-mono text-xs"
                    data-testid="device-public-url"
                  />
                  <Button size="sm" onClick={async () => {
                    const r = await fetch(`${API}/solar/device`, {
                      method: 'PUT', headers,
                      body: JSON.stringify({ public_base_url: (device.public_base_url || '').trim() })
                    });
                    if (r.ok) { setDevice(await r.json()); toast.success('Public URL kaydedildi'); }
                    else { const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Kaydedilemedi'); }
                  }} className="bg-emerald-600 hover:bg-emerald-700">
                    <Save className="h-3.5 w-3.5 mr-1" />Kaydet
                  </Button>
                </div>
                <p className="text-[10px] text-slate-500 mt-1">
                  Boş bırakırsanız otomatik olarak <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">{window.location.origin}</code> kullanılır. Farklı bir sub-domain/reverse-proxy kullanıyorsanız (örn. api.weddingtakip.com) buraya yazın. <b>Mutlaka http:// veya https:// ile başlasın.</b>
                </p>
              </div>

              {/* API Key */}
              <div>
                <Label className="flex items-center gap-1"><Cpu className="h-3.5 w-3.5" />Cihaz API Key</Label>
                <div className="flex gap-2 mt-1">
                  <Input type={showKey ? 'text' : 'password'} value={device.api_key} readOnly className="font-mono text-xs" data-testid="device-api-key" />
                  <Button size="sm" variant="outline" onClick={() => setShowKey(v => !v)}>{showKey ? 'Gizle' : 'Göster'}</Button>
                  <Button size="sm" variant="outline" onClick={() => copyText(device.api_key, 'API key kopyalandı')} data-testid="copy-api-key">
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={rotateKey} className="text-red-600 hover:text-red-700" title="Yeni API key oluştur">
                    <RefreshCw className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <p className="text-[10px] text-slate-500 mt-1">Bu key Arduino/ESP32'nin backend'e bağlanmak için kullanacağı gizli anahtardır. Yenilerseniz eski cihaz artık gönderim yapamaz.</p>
              </div>

              {/* Eşik */}
              <div>
                <Label>Sinyal Kesilme Eşiği (saniye)</Label>
                <div className="flex gap-2 mt-1">
                  <Input type="number" min="30" value={device.threshold_seconds || 300} onChange={e => setDevice({ ...device, threshold_seconds: e.target.value })} data-testid="device-threshold" />
                  <Button size="sm" onClick={() => saveDeviceThreshold(Number(device.threshold_seconds) || 300)} className="bg-emerald-600 hover:bg-emerald-700">
                    <Save className="h-3.5 w-3.5 mr-1" />Kaydet
                  </Button>
                </div>
                <p className="text-[10px] text-slate-500 mt-1">Cihazdan bu süre boyunca sinyal gelmezse "cihaz bağlantısı kesik" uyarısı gösterilir. Cihaz gönderim aralığından en az 3× büyük olsun (60sn'de bir gönderiyorsa 180-300sn). Önerilen: 300 sn.</p>
              </div>

              {/* Sessiz saatler */}
              <div className="bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                <Label className="flex items-center gap-1 text-amber-800 dark:text-amber-300"><Moon className="h-3.5 w-3.5" />Sessiz Saatler (Gece — üretim beklenmiyor)</Label>
                <p className="text-[10px] text-amber-700 dark:text-amber-400 mb-2">Bu aralıkta üretim durursa <b>kırmızı alarm yerine sarı bilgi</b> gösterilir. Türkiye saatiyle (UTC+3). Boş bırakırsanız 7/24 alarm aktif.</p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px]">Başlangıç (HH:MM)</Label>
                    <Input type="time" value={device.quiet_hours_start || ''} onChange={e => setDevice({ ...device, quiet_hours_start: e.target.value })} data-testid="quiet-start" />
                  </div>
                  <div>
                    <Label className="text-[10px]">Bitiş (HH:MM)</Label>
                    <Input type="time" value={device.quiet_hours_end || ''} onChange={e => setDevice({ ...device, quiet_hours_end: e.target.value })} data-testid="quiet-end" />
                  </div>
                </div>
                <div className="flex justify-between items-center mt-2">
                  <p className="text-[10px] text-amber-700 dark:text-amber-400">
                    Örn: <b>18:00 → 07:00</b> (gün doğumu-batımı)
                  </p>
                  <Button size="sm" onClick={saveQuietHours} className="bg-amber-600 hover:bg-amber-700 h-7 text-xs" data-testid="quiet-save">
                    <Save className="h-3 w-3 mr-1" />Kaydet
                  </Button>
                </div>
              </div>

              {/* Endpoint bilgisi */}
              <div className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-lg p-3">
                <div className="text-xs font-semibold text-slate-700 dark:text-slate-200 mb-2">📡 Arduino'ya Yazılacak URL (TAM)</div>
                <div className="flex gap-2">
                  <code className="flex-1 text-[11px] bg-white dark:bg-slate-900 p-2 rounded border border-slate-200 dark:border-slate-700 font-mono break-all">
                    {fullTelemetryUrl}
                  </code>
                  <Button size="sm" variant="outline" onClick={() => copyText(fullTelemetryUrl, 'URL kopyalandı')}>
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
                {isHttps && (
                  <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-1">
                    ⚠️ HTTPS adres — ESP32'de <code className="bg-amber-100 dark:bg-amber-900/40 px-1 rounded">http.setInsecure()</code> gerekir (örnek kod içinde hazır).
                  </p>
                )}
                <div className="text-[11px] text-slate-600 dark:text-slate-300 mt-2">
                  <b>Method: POST · Body örneği (JSON):</b>
                  <pre className="bg-white dark:bg-slate-900 p-2 rounded border border-slate-200 dark:border-slate-700 font-mono mt-1 overflow-x-auto">{`{
  "api_key": "${device.api_key.substring(0, 12)}...",
  "status": "on",
  "watts": 1250
}`}</pre>
                  <p className="mt-1">Röle ile takip için <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">status</code> alanı yeterli ("on" / "off"). <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">watts</code> opsiyonel.</p>
                </div>
              </div>

              {/* Örnek Kod - Board Seçici */}
              <details className="border border-slate-200 dark:border-slate-700 rounded-lg" open>
                <summary className="cursor-pointer px-3 py-2 text-sm font-medium bg-slate-50 dark:bg-slate-800/40 rounded-t-lg flex items-center gap-2">
                  <Cpu className="h-4 w-4 text-emerald-600" />Arduino Örnek Kod (Röle/Dijital Pin ile)
                </summary>
                <div className="p-3 space-y-2">
                  <div className="flex gap-1 bg-slate-100 dark:bg-slate-800 rounded-lg p-1 w-fit">
                    <button
                      onClick={() => setBoardType('esp32')}
                      className={`px-3 py-1 text-xs rounded font-medium transition-colors ${boardType === 'esp32' ? 'bg-white dark:bg-slate-700 text-emerald-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                      data-testid="board-esp32"
                    >ESP32</button>
                    <button
                      onClick={() => setBoardType('esp8266')}
                      className={`px-3 py-1 text-xs rounded font-medium transition-colors ${boardType === 'esp8266' ? 'bg-white dark:bg-slate-700 text-emerald-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                      data-testid="board-esp8266"
                    >ESP8266 / NodeMCU</button>
                  </div>
                  <pre className="bg-slate-900 text-emerald-100 text-[10.5px] p-3 rounded overflow-x-auto font-mono leading-relaxed" data-testid="arduino-code">{
                    boardType === 'esp32'
                      ? `#include <WiFi.h>
#include <HTTPClient.h>${isHttps ? '\n#include <WiFiClientSecure.h>' : ''}

const char* WIFI_SSID = "wifi_adiniz";
const char* WIFI_PASS = "wifi_sifresi";
const char* API_URL   = "${fullTelemetryUrl}";
const char* API_KEY   = "${device.api_key}";
const int   RELAY_PIN = 34;   // röle/kontak girişi (GPIO34, INPUT)
const unsigned long INTERVAL_MS = 60000; // 1 dk

void setup() {
  Serial.begin(115200);
  pinMode(RELAY_PIN, INPUT_PULLUP);
  WiFi.begin(WIFI_SSID, WIFI_PASS);
  while (WiFi.status() != WL_CONNECTED) { delay(500); Serial.print("."); }
  Serial.println("\\nWiFi hazir: " + WiFi.localIP().toString());
}

void sendHeartbeat(const char* status) {
  if (WiFi.status() != WL_CONNECTED) return;${isHttps ? '\n  WiFiClientSecure client;\n  client.setInsecure();  // sertifika dogrulamayi kapat\n  HTTPClient http;\n  http.begin(client, API_URL);' : '\n  HTTPClient http;\n  http.begin(API_URL);'}
  http.addHeader("Content-Type", "application/json");
  String body = String("{\\"api_key\\":\\"") + API_KEY +
                "\\",\\"status\\":\\"" + status + "\\"}";
  int code = http.POST(body);
  Serial.printf("POST %s => %d\\n", status, code);
  http.end();
}

void loop() {
  int val = digitalRead(RELAY_PIN);
  // Rolenin kapali oldugu durum = uretim var (kendi devrenize gore ters cevirebilirsiniz)
  sendHeartbeat(val == LOW ? "on" : "off");
  delay(INTERVAL_MS);
}`
                      : `#include <ESP8266WiFi.h>
#include <ESP8266HTTPClient.h>${isHttps ? '\n#include <WiFiClientSecureBearSSL.h>' : '\n#include <WiFiClient.h>'}

const char* WIFI_SSID = "wifi_adiniz";
const char* WIFI_PASS = "wifi_sifresi";
const char* API_URL   = "${fullTelemetryUrl}";
const char* API_KEY   = "${device.api_key}";
const int   RELAY_PIN = D5;   // D5 = GPIO14 (röle/kontak girişi)
const unsigned long INTERVAL_MS = 60000; // 1 dk

void setup() {
  Serial.begin(115200);
  pinMode(RELAY_PIN, INPUT_PULLUP);
  WiFi.begin(WIFI_SSID, WIFI_PASS);
  while (WiFi.status() != WL_CONNECTED) { delay(500); Serial.print("."); }
  Serial.println("\\nWiFi hazir: " + WiFi.localIP().toString());
}

void sendHeartbeat(const char* status) {
  if (WiFi.status() != WL_CONNECTED) return;${isHttps ? '\n  std::unique_ptr<BearSSL::WiFiClientSecure> client(new BearSSL::WiFiClientSecure);\n  client->setInsecure();  // sertifika dogrulamayi kapat\n  HTTPClient http;\n  http.begin(*client, API_URL);' : '\n  WiFiClient client;\n  HTTPClient http;\n  http.begin(client, API_URL);'}
  http.addHeader("Content-Type", "application/json");
  String body = String("{\\"api_key\\":\\"") + API_KEY +
                "\\",\\"status\\":\\"" + status + "\\"}";
  int code = http.POST(body);
  Serial.printf("POST %s => %d\\n", status, code);
  http.end();
}

void loop() {
  int val = digitalRead(RELAY_PIN);
  // Rolenin kapali oldugu durum = uretim var
  sendHeartbeat(val == LOW ? "on" : "off");
  delay(INTERVAL_MS);
}`
                  }</pre>
                  <Button size="sm" variant="outline" onClick={() => copyText(document.querySelector('[data-testid="arduino-code"]')?.innerText || '', 'Kod kopyalandı')} className="w-full">
                    <Copy className="h-3.5 w-3.5 mr-1" />Örnek Kodu Kopyala
                  </Button>
                  <div className="text-[10px] text-slate-500 space-y-0.5">
                    {boardType === 'esp8266' ? (
                      <>
                        <p>📌 <b>NodeMCU pin karşılıkları:</b> D5=GPIO14, D6=GPIO12, D7=GPIO13. D0 girişte pull-up desteklemez, D5-D7 önerilir.</p>
                        <p>⚙️ <b>Arduino IDE:</b> Board Manager'dan "ESP8266 by ESP8266 Community" paketini yükleyin. Board: "NodeMCU 1.0 (ESP-12E Module)".</p>
                      </>
                    ) : (
                      <>
                        <p>📌 <b>ESP32 pin:</b> GPIO34/35/36/39 sadece giriş (INPUT), pull-up yok — devrede 10kΩ pull-up direnç ekleyin. Ya da GPIO2/4/5 gibi normal pinleri kullanın.</p>
                        <p>⚙️ <b>Arduino IDE:</b> Board Manager'dan "esp32 by Espressif Systems" paketini yükleyin. Board: "ESP32 Dev Module".</p>
                      </>
                    )}
                    <p>🔌 Röle'yi ters bağladıysanız <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">== HIGH</code> yapın.</p>
                  </div>
                </div>
              </details>

              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setShowLive(false)}>Kapat</Button>
              </div>
            </div>
          );})()}
        </DialogContent>
      </Dialog>

      {/* Yıl Kıyaslama Dialog */}
      <Dialog open={showCompare} onOpenChange={setShowCompare}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><GitCompare className="h-5 w-5 text-blue-600" />Yıl Bazlı Kıyaslama</DialogTitle>
          </DialogHeader>
          {compareData.length === 0 ? (
            <p className="text-center text-sm text-slate-400 py-8">Kıyaslamak için en az bir yıllık kayıt olmalı.</p>
          ) : (
            <div className="space-y-4">
              {/* Üretim vs Tüketim yıllık */}
              <div>
                <div className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">Yıllık Üretim vs Tüketim (kWh)</div>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={compareData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => `${fmtInt(v)} kWh`} />
                    <Legend />
                    <Bar dataKey="production" name="Üretim" fill="#eab308" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="consumption" name="Tüketim" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {/* Kazanç yıllık */}
              <div>
                <div className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">Yıllık Kazanç Detayı (TL)</div>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={compareData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => `${fmt(v)} TL`} />
                    <Legend />
                    <Bar dataKey="savings" name="Tasarruf" fill="#10b981" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="surplus" name="Fazla Satış" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="grid_payment" name="Şebeke Ödemesi" fill="#dc2626" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {/* Tablo */}
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 dark:bg-slate-800">
                    <tr>
                      <th className="text-left p-2">Yıl</th>
                      <th className="text-right p-2">Ay</th>
                      <th className="text-right p-2">Üretim (kWh)</th>
                      <th className="text-right p-2">Tüketim (kWh)</th>
                      <th className="text-right p-2">Toplam Kazanç</th>
                      <th className="text-right p-2">Net Kazanç</th>
                    </tr>
                  </thead>
                  <tbody>
                    {compareData.map(y => (
                      <tr key={y.year} className="border-t border-slate-100 dark:border-slate-800">
                        <td className="p-2 font-bold">{y.year}</td>
                        <td className="p-2 text-right text-slate-500">{y.months}</td>
                        <td className="p-2 text-right font-mono text-yellow-700">{fmtInt(y.production)}</td>
                        <td className="p-2 text-right font-mono text-blue-700">{fmtInt(y.consumption)}</td>
                        <td className="p-2 text-right font-mono font-semibold text-emerald-700">{fmt(y.gain)} ₺</td>
                        <td className={`p-2 text-right font-mono font-semibold ${y.net_gain >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{fmt(y.net_gain)} ₺</td>
                      </tr>
                    ))}
                    {compareData.length >= 2 && (() => {
                      const first = compareData[0], last = compareData[compareData.length - 1];
                      const pctChange = (a, b) => a > 0 ? ((b - a) / a * 100) : 0;
                      const prodChg = pctChange(first.production, last.production);
                      const gainChg = pctChange(first.gain, last.gain);
                      return (
                        <tr className="bg-blue-50 dark:bg-blue-900/20 border-t-2 border-blue-300">
                          <td className="p-2 font-bold text-blue-700 dark:text-blue-300">{first.year} → {last.year}</td>
                          <td></td>
                          <td className={`p-2 text-right font-mono font-bold ${prodChg >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{prodChg >= 0 ? '+' : ''}{fmt(prodChg)}%</td>
                          <td></td>
                          <td className={`p-2 text-right font-mono font-bold ${gainChg >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{gainChg >= 0 ? '+' : ''}{fmt(gainChg)}%</td>
                          <td></td>
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <div className="flex justify-end pt-2">
            <Button variant="outline" onClick={() => setShowCompare(false)}>Kapat</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Aylık Kayıt Dialog */}
      <Dialog open={showRec} onOpenChange={setShowRec}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sun className="h-5 w-5 text-yellow-500" />
              {editing ? 'Aylık Kaydı Düzenle' : 'Yeni Aylık Kayıt'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Dönem (Ay/Yıl)</Label>
              <Input type="month" value={recForm.period} onChange={e => setRecForm({ ...recForm, period: e.target.value })} disabled={!!editing} data-testid="solar-period" />
              <p className="text-[10px] text-slate-500 mt-0.5">Aynı ay tekrar eklenirse üzerine yazılır</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Üretim (kWh)</Label>
                <Input type="number" step="0.01" value={recForm.production_kwh} onChange={e => setRecForm({ ...recForm, production_kwh: e.target.value })} placeholder="38565" data-testid="solar-prod" />
              </div>
              <div>
                <Label>Ana Tüketim (kWh)</Label>
                <Input type="number" step="0.01" value={recForm.consumption_kwh} onChange={e => setRecForm({ ...recForm, consumption_kwh: e.target.value })} placeholder="24500" data-testid="solar-cons" />
                {extraBillKwh > 0 && (
                  <p className="text-[10px] text-violet-600 mt-0.5">+ {fmtInt(extraBillKwh)} kWh abone faturası → Toplam: {fmtInt(totalCons)} kWh</p>
                )}
              </div>
              <div>
                <Label>Tüketim Birim Fiyatı (TL/kWh)</Label>
                <Input type="number" step="0.0001" value={recForm.buy_price} onChange={e => setRecForm({ ...recForm, buy_price: e.target.value })} placeholder="3.15" />
              </div>
              <div>
                <Label>Üretim (Satış) Fiyatı (TL/kWh)</Label>
                <Input type="number" step="0.0001" value={recForm.sell_price} onChange={e => setRecForm({ ...recForm, sell_price: e.target.value })} placeholder="2.85" />
              </div>
            </div>

            {/* Abone Fatura Girişi */}
            <div className="border border-violet-200 dark:border-violet-800 rounded-lg p-3 bg-violet-50/40 dark:bg-violet-900/10">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium text-violet-800 dark:text-violet-300 flex items-center gap-1.5">
                  <Users className="h-4 w-4" />Abone Faturaları
                </div>
                <Button size="sm" variant="outline" onClick={addBillRow} disabled={subscribers.length === 0} data-testid="add-bill-row">
                  <Plus className="h-3.5 w-3.5 mr-1" />Fatura Ekle
                </Button>
              </div>
              {subscribers.length === 0 ? (
                <p className="text-xs text-slate-500 py-2">Önce <button className="text-violet-600 underline" onClick={() => { setShowRec(false); setShowSubs(true); }}>Aboneler</button> bölümünden abone ekleyin.</p>
              ) : recForm.subscriber_bills.length === 0 ? (
                <p className="text-xs text-slate-500 py-2">Bu aya ait abone faturaları eklenmedi. "Fatura Ekle" ile başlayın.</p>
              ) : (
                <div className="space-y-2">
                  {recForm.subscriber_bills.map((bill, idx) => (
                    <div key={idx} className="bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 p-2">
                      <div className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-12 sm:col-span-4">
                          <Label className="text-[10px]">Abone</Label>
                          <Select value={bill.subscriber_id} onValueChange={(v) => onBillSubscriberChange(idx, v)}>
                            <SelectTrigger className="h-8 text-xs" data-testid={`bill-sub-${idx}`}><SelectValue placeholder="Seç" /></SelectTrigger>
                            <SelectContent>
                              {subscribers.map(s => (
                                <SelectItem key={s.id} value={s.id}>{s.name} {s.subscriber_no ? `(${s.subscriber_no})` : ''}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-4 sm:col-span-2">
                          <Label className="text-[10px]">Tüketim (kWh)</Label>
                          <Input type="number" className="h-8 text-xs" step="0.01" value={bill.kwh} onChange={(e) => updateBillRow(idx, { kwh: e.target.value })} data-testid={`bill-kwh-${idx}`} />
                        </div>
                        <div className="col-span-4 sm:col-span-2">
                          <Label className="text-[10px]">Fatura (TL)</Label>
                          <Input type="number" className="h-8 text-xs" step="0.01" value={bill.amount} onChange={(e) => updateBillRow(idx, { amount: e.target.value })} />
                        </div>
                        <div className="col-span-4 sm:col-span-3 flex items-center gap-1.5 pb-1.5">
                          <Checkbox id={`inc-${idx}`} checked={bill.include_in_total} onCheckedChange={(v) => updateBillRow(idx, { include_in_total: !!v })} data-testid={`bill-inc-${idx}`} />
                          <label htmlFor={`inc-${idx}`} className="text-[11px] text-slate-600 dark:text-slate-300 cursor-pointer select-none">Toplama ekle</label>
                        </div>
                        <div className="col-span-12 sm:col-span-1 flex justify-end">
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => removeBillRow(idx)}>
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                      {bill.note !== undefined && (
                        <Input className="mt-1 h-7 text-xs" placeholder="Not (isteğe bağlı)" value={bill.note || ''} onChange={(e) => updateBillRow(idx, { note: e.target.value })} />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <Label>Notlar</Label>
              <Input value={recForm.notes} onChange={e => setRecForm({ ...recForm, notes: e.target.value })} placeholder="İsteğe bağlı" />
            </div>

            {/* Anlık önizleme */}
            {(recForm.production_kwh || recForm.consumption_kwh || extraBillKwh > 0) && (
              <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-3 text-xs space-y-1">
                <div className="flex justify-between">
                  <span>Toplam Tüketim:</span>
                  <b>{fmtInt(totalCons)} kWh {extraBillKwh > 0 && <span className="text-violet-600">(ana + {fmtInt(extraBillKwh)} abone)</span>}</b>
                </div>
                <div className="flex justify-between">
                  <span>Net kWh (Üretim − Tüketim):</span>
                  <b className={netKwh >= 0 ? 'text-emerald-700' : 'text-red-600'}>{netKwh >= 0 ? '+' : ''}{fmtInt(netKwh)} kWh</b>
                </div>
                <div className="flex justify-between">
                  <span>Öz Tüketim (Tasarruf):</span>
                  <b>{fmt(Math.min(Number(recForm.production_kwh) || 0, totalCons) * (Number(recForm.buy_price) || 0))} TL</b>
                </div>
                <div className="flex justify-between">
                  <span>Fazla Satış:</span>
                  <b>{fmt(Math.max(0, (Number(recForm.production_kwh) || 0) - totalCons) * (Number(recForm.sell_price) || 0))} TL</b>
                </div>
                <div className="flex justify-between border-t pt-1 mt-1 border-emerald-200">
                  <span className="font-semibold">Toplam Kazanç:</span>
                  <b className="text-emerald-700 text-sm">
                    {fmt(
                      Math.min(Number(recForm.production_kwh) || 0, totalCons) * (Number(recForm.buy_price) || 0)
                      + Math.max(0, (Number(recForm.production_kwh) || 0) - totalCons) * (Number(recForm.sell_price) || 0)
                    )} TL
                  </b>
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowRec(false)}>İptal</Button>
              <Button onClick={saveRec} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="solar-save-record">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" />Kaydet</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon: Icon, color, label, value, unit, big }) {
  return (
    <Card>
      <CardContent className="p-3">
        <div className={`w-8 h-8 rounded-lg ${color} flex items-center justify-center mb-2`}>
          <Icon className="h-4 w-4" />
        </div>
        <p className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</p>
        <p className={`font-bold text-slate-800 dark:text-slate-100 ${big ? 'text-lg' : 'text-base'}`}>
          {value} <span className="text-xs text-slate-400 font-normal">{unit}</span>
        </p>
      </CardContent>
    </Card>
  );
}
